import type { TranslateFn } from "@app/_utils/interpolate";
import { z } from "zod";

import { getZipCodeRegexByMarketCode } from "@/config/market-configurations";
import type { MarketCode } from "@/config/markets/const";

export function getZipCodeOnlySchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    zipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
  });
}

export function getAddressSchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    firstName: z
      .string()
      .min(1, t("firstNameRequired") ?? "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z
      .string()
      .min(1, t("emailRequired") ?? "Email is required")
      .email(t("emailInvalid") ?? "Invalid email address"),
    company: z.string().optional(),
    phoneNumber: z
      .string()
      .min(1, t("phoneNumberRequired") ?? "Phone number is required"),
    line1: z
      .string()
      .min(1, t("streetRequired") ?? "Street address is required"),
    line2: z.string().optional(),
    city: z.string().min(1, t("cityRequired") ?? "City is required"),
    zipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
    country: z.string().optional(),
  });
}

export function getDeliveryAddressSchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    deliveryFirstName: z
      .string()
      .min(1, t("firstNameRequired") ?? "First name is required"),
    deliveryLastName: z
      .string()
      .min(1, t("lastNameRequired") ?? "Last name is required"),
    deliveryLine1: z
      .string()
      .min(1, t("streetRequired") ?? "Street address is required"),
    deliveryCity: z.string().min(1, t("cityRequired") ?? "City is required"),
    deliveryZipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
    deliveryCountry: z.string().optional(),
  });
}

export const nShiftDeliveryOptionSchema = z.object({
  valid: z.boolean(),
  deliveryCheckoutId: z.string(),
  selectedOptionId: z.string(),
  optionIds: z.array(z.string()),
  price: z.number(),
  agent: z.string().optional(),
  selectedAddons: z.array(z.unknown()),
  fields: z.array(z.unknown()),
  taxRate: z.number(),
});

/**
 * Phone number validation is shared with the v3 checkout so that both flows
 * accept the same numbers. See features/checkout/utils/addressSchema.ts.
 */
export {
  ALWAYS_ALLOWED_PHONE_COUNTRIES,
  superRefinePhoneNumber,
  transformPhoneNumber,
} from "@features/checkout/utils/addressSchema";
